import NoDataIcon from '@/modules/Statistics/assets/icons/NoData.svg?react';
import {
  BarController,
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  ChartType,
  Legend,
  LinearScale,
  Plugin,
  ScriptableContext,
  Tooltip,
} from 'chart.js';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useSmoothScrollToCurrentMonth } from '../../hooks/useSmoothScrollToCurrentMonth';
import { PerformanceBarChartProps, PerformanceChartTheme } from './Chart.type';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  BarController,
);

const FONT_FAMILY = "'iran-yekan', sans-serif";

declare module 'chart.js' {
  interface PluginOptionsByType<TType extends ChartType> {
    averageLinePlugin?: {
      average?: number;
      color?: string;
      dash?: number[];
      width?: number;
    };
  }
}

const THEMES: Record<
  PerformanceChartTheme,
  { solid: string; top: string; bottom: string }
> = {
  green: {
    solid: '#2DB788',
    top: '#33D59F',
    bottom: '#2DB788',
  },
  blue: {
    solid: '#5CC0EB',
    top: '#99D2EA',
    bottom: '#5CC0EB',
  },
  orange: {
    solid: '#FF9A3C',
    top: '#FFB977',
    bottom: '#FF9A3C',
  },
  red: {
    solid: '#E34C4C',
    top: '#FF9494',
    bottom: '#E34C4C',
  },
  yellow: {
    solid: '#F0B707',
    top: '#F4D576',
    bottom: '#F0B707',
  },
};

const TOOLTIP_DOT_COLORS: Record<PerformanceChartTheme, string> = {
  green: '#0DC277',
  blue: '#5EC1EB',
  orange: '#FC8516',
  red: '#E45050',
  yellow: '#F0B707',
};

const MAX_BAR_COLOR = 'rgba(0,0,0,0.06)';
const MAX_TOOLTIP_DOT_COLOR = '#EBEBEB';

const Y_AXIS_MIN_WIDTH = 32;
const Y_AXIS_MAX_WIDTH = 64;
const Y_AXIS_CHAR_WIDTH = 12;
const Y_AXIS_PADDING = 8;

const SCROLLBAR_HEIGHT_FALLBACK = 16;
const SCROLL_GAP = 8;

function makeBarGradient(
  ctx: CanvasRenderingContext2D,
  area: { top: number; bottom: number },
  theme: PerformanceChartTheme,
) {
  const g = ctx.createLinearGradient(0, area.top, 0, area.bottom);
  g.addColorStop(0, THEMES[theme].bottom);
  g.addColorStop(1, THEMES[theme].top);
  return g;
}

const averageLinePlugin: Plugin<'bar'> = {
  id: 'averageLinePlugin',
  afterDatasetsDraw: (chart) => {
    const pluginOptions = chart.options.plugins?.averageLinePlugin;

    const average = pluginOptions?.average;
    const color = pluginOptions?.color ?? '#2F80ED';
    const rawDash = pluginOptions?.dash;
    const dash = Array.isArray(rawDash)
      ? rawDash.filter((n): n is number => typeof n === 'number')
      : [6, 6];

    const width = pluginOptions?.width ?? 1;

    if (average == null) return;

    const { ctx, chartArea, scales } = chart;
    const yScale = scales.y;
    const y = yScale.getPixelForValue(average);

    if (y < chartArea.top || y > chartArea.bottom) return;

    ctx.save();
    ctx.beginPath();
    ctx.setLineDash(dash);
    ctx.lineWidth = width;
    ctx.strokeStyle = color;
    ctx.moveTo(chartArea.left, y);
    ctx.lineTo(chartArea.right, y);
    ctx.stroke();
    ctx.restore();
  },
};

const hideEmptyTooltipPlugin: Plugin<'bar'> = {
  id: 'hideEmptyTooltipPlugin',
  beforeTooltipDraw: (chart) => {
    const dataPoints = chart.tooltip?.dataPoints;

    if (!dataPoints?.length) return false;

    return dataPoints.some((item) => Number(item.raw) > 0);
  },
};

export default function BarChart({
  labels,
  values,
  maxValues,
  average,
  theme = 'green',
  height = 200,
  valueSuffix,
  integerValues = false,
  axisMax,
}: PerformanceBarChartProps) {
  const [animate, setAnimate] = useState(false);
  const [scrollbarThickness, setScrollbarThickness] = useState(
    SCROLLBAR_HEIGHT_FALLBACK,
  );
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const hasValidData = useMemo(() => {
    const result = values.some((value) => Number(value) > 0);
    return result;
  }, [values]);

  useEffect(() => {
    if (!hasValidData || !scrollRef.current) {
      setAnimate(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setAnimate(entry.isIntersecting);
      },
      {
        threshold: 0.3,
      },
    );

    observer.observe(scrollRef.current);

    return () => observer.disconnect();
  }, [hasValidData]);

  useLayoutEffect(() => {
    const element = scrollRef.current;

    if (!element) return undefined;

    const sync = () => {
      setScrollbarThickness(element.offsetHeight - element.clientHeight);
    };

    sync();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(sync);
    observer.observe(element);

    return () => observer.disconnect();
  }, [hasValidData]);

  const resolvedMaxValues = useMemo(() => {
    if (maxValues?.length === labels.length) return maxValues;

    return labels.map((_, i) => {
      const currentValue = values[i] ?? 0;
      const base = Math.max(currentValue, average ?? 0, 1);
      return Math.ceil(base * 1.15);
    });
  }, [maxValues, labels, values, average]);

  const yMax = useMemo(() => {
    const max = Math.max(...resolvedMaxValues, average ?? 0, 1);
    return Math.ceil(max * 1.15);
  }, [resolvedMaxValues, average]);

  const yAxisWidth = useMemo(() => {
    const widestTick = Math.max(axisMax ?? yMax, 0);
    const chars = widestTick.toLocaleString('fa-IR').length;

    return Math.min(
      Math.max(chars * Y_AXIS_CHAR_WIDTH + Y_AXIS_PADDING, Y_AXIS_MIN_WIDTH),
      Y_AXIS_MAX_WIDTH,
    );
  }, [axisMax, yMax]);

  const data = useMemo<ChartData<'bar'>>(() => {
    const datasets: any[] = [];
    if (maxValues?.length === labels.length) {
      datasets.push({
        label: 'max',
        data: resolvedMaxValues,
        backgroundColor: MAX_BAR_COLOR,
        borderRadius: 8,
        borderSkipped: false,
        barThickness: 28,
        grouped: false,
        order: 1,
      });
    }

    datasets.push({
      label: 'value',
      data: values,
      backgroundColor: (c: ScriptableContext<'bar'>) => {
        const { chart } = c;
        const { ctx, chartArea } = chart;
        if (!chartArea) return THEMES[theme].solid;
        return makeBarGradient(ctx, chartArea, theme);
      },
      borderRadius: 8,
      borderSkipped: false,
      barThickness: 28,
      grouped: false,
      order: 2,
    });

    return { labels, datasets };
  }, [labels, values, resolvedMaxValues, theme, maxValues]);

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 2000,
        easing: 'easeOutExpo',
      },
      layout: { padding: { top: 12, right: 8, left: 0, bottom: 6 } },
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          rtl: true,
          textDirection: 'rtl',
          backgroundColor: '#3C3C3C',
          bodyColor: '#fff',

          displayColors: true,
          usePointStyle: true,
          boxPadding: 8,
          padding: 10,

          position: 'nearest',
          yAlign: 'bottom',
          xAlign: 'center',
          caretSize: 0,
          caretPadding: 15,

          bodyFont: {
            size: 14,
            family: FONT_FAMILY,
          },

          filter: (ctx) => {
            const value = Number(values[ctx.dataIndex]);
            const maxValue = Number(resolvedMaxValues[ctx.dataIndex]);

            if (!Number.isFinite(value) || value <= 0) return false;

            return !(ctx.dataset.label === 'max' && value === maxValue);
          },

          callbacks: {
            title: () => '',

            label: (ctx) => {
              const raw = Number(ctx.raw);
              const v = integerValues ? Math.round(raw) : raw;
              return `${v.toLocaleString('fa-IR')}${valueSuffix ? ` ${valueSuffix}` : ''}`;
            },

            labelColor: (ctx) => ({
              borderColor: 'transparent',
              backgroundColor:
                ctx.dataset.label === 'max'
                  ? MAX_TOOLTIP_DOT_COLOR
                  : TOOLTIP_DOT_COLORS[theme],
              borderWidth: 0,
              borderRadius: 999,
            }),

            labelPointStyle: () => ({
              pointStyle: 'circle',
              rotation: 0,
            }),
          },
        },

        // averageLinePlugin: {
        //   average,
        //   color: '#2F80ED',
        //   dash: [6, 6],
        //   width: 1,
        // },
      },
      scales: {
        x: {
          offset: true,
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: '#787878',
            font: { size: 12, family: FONT_FAMILY },
            minRotation: 0,
            maxRotation: 0,
          },
        },
        y: {
          beginAtZero: true,
          suggestedMax: axisMax ?? yMax,
          grid: {
            color: 'rgba(0,0,0,0.06)',
            drawTicks: false,
          },
          border: { display: false },
          ticks: {
            display: false,
            precision: integerValues ? 0 : undefined,
          },
          afterFit: (scaleInstance) => {
            scaleInstance.width = 0;
          },
        },
      },
    }),
    [
      average,
      theme,
      valueSuffix,
      yMax,
      integerValues,
      axisMax,
      values,
      resolvedMaxValues,
    ],
  );

  const axisData = useMemo<ChartData<'bar'>>(() => {
    const scaleSource =
      maxValues?.length === labels.length ? resolvedMaxValues : values;
    const dataMax = scaleSource.length ? Math.max(...scaleSource) : 0;

    return {
      labels: [''],
      datasets: [
        {
          data: [dataMax],
          backgroundColor: 'transparent',
        },
      ],
    };
  }, [labels, values, resolvedMaxValues, maxValues]);

  const axisOptions = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      events: [],
      layout: { padding: { top: 12, right: 2, left: 0, bottom: 6 } },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: {
            display: false,
            font: { size: 12, family: FONT_FAMILY },
            minRotation: 0,
            maxRotation: 0,
          },
        },
        y: {
          position: 'left',
          beginAtZero: true,
          suggestedMax: axisMax ?? yMax,
          grid: { display: false },
          border: { display: false },
          ticks: {
            precision: integerValues ? 0 : undefined,
            color: '#787878',
            font: { size: 12, family: FONT_FAMILY },
            align: 'center',
            crossAlign: 'far',
            padding: 4,
            callback: (v) => `\u200E${Number(v).toLocaleString('fa-IR')}`,
          },
          afterFit: (scaleInstance) => {
            scaleInstance.width = yAxisWidth;
          },
        },
      },
    }),
    [axisMax, yMax, integerValues, yAxisWidth],
  );

  const visibleBars = 6;
  const minBarSlot = 72;
  const chartWidth = Math.max(labels.length, visibleBars) * minBarSlot;

  useSmoothScrollToCurrentMonth(scrollRef, labels, minBarSlot);

  if (!hasValidData) {
    return (
      <div
        style={{
          width: '100%',
          minHeight: height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '12px',
          color: '#9CA3AF',
          textAlign: 'center',
        }}
      >
        <NoDataIcon />
        <span
          style={{
            fontSize: '14px',
            fontFamily: FONT_FAMILY,
            color: '#787878',
          }}
        >
          اطلاعاتی موجود نیست.
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        direction: 'ltr',
      }}
    >
      <div
        style={{
          flex: `0 0 ${yAxisWidth}px`,
          width: yAxisWidth,
          height,
        }}
      >
        {animate && <Bar data={axisData} options={axisOptions} plugins={[]} />}
      </div>

      <div
        style={{
          flex: '1 1 0',
          minWidth: 0,
          height: height + scrollbarThickness + SCROLL_GAP,
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
        ref={scrollRef}
      >
        <div style={{ width: chartWidth, minWidth: '100%', height }}>
          {animate && (
            <Bar
              data={data}
              options={options}
              plugins={[averageLinePlugin, hideEmptyTooltipPlugin]}
            />
          )}
        </div>
      </div>
    </div>
  );
}
